import { Select } from '@approvalmax/ui/src/components';
import size from 'lodash/size';
import uniqueId from 'lodash/uniqueId';
import { selectors } from 'modules/common';
import { backend, domain } from 'modules/data';
import { Dispatch, SetStateAction } from 'react';
import { notificationService } from 'services/notification';
import { CustomFieldAccessLevel, customFieldAccessLevels, CustomFieldLevel, CustomFieldType } from 'shared/constants';

import { NetSuiteCustomFieldReferenceItem } from '../../types';
import { netSuiteAccessLevelTypes, netSuiteIntegrationCodes } from './NetSuiteCustomFields.constants';
import { messages } from './NetSuiteCustomFields.messages';
import {
    AccessLevelFieldKeys,
    CustomFieldsStateItem,
    FieldsByIntegrationCode,
    ValidateFieldChangesArgs,
    WorkflowAccessLevelType,
} from './NetSuiteCustomFields.types';

export const mapLevelToBackendValue = (level?: string) => {
    switch (level) {
        case CustomFieldLevel.Header:
            return 0;

        case CustomFieldLevel.Lines:
            return 1;

        case CustomFieldLevel.HeaderAndLines:
            return 2;

        default:
            return 2;
    }
};

export const isCorrectCustomFieldType = (fieldType: CustomFieldType, fieldScriptId: string) =>
    (fieldType === CustomFieldType.Body && fieldScriptId.startsWith('custbody')) ||
    (fieldType === CustomFieldType.Line && fieldScriptId.startsWith('custcol')) ||
    (fieldType === CustomFieldType.Segment && fieldScriptId.startsWith('cseg'));

export const mapWorkflowFieldAccessLevel = (workflowSetting?: backend.NetSuiteCustomFieldWorkflowData) => {
    if (!workflowSetting) {
        return CustomFieldAccessLevel.Editable;
    }

    if (!workflowSetting.IsActive) {
        return CustomFieldAccessLevel.Hidden;
    }

    if (workflowSetting.IsMandatory) {
        return CustomFieldAccessLevel.Mandatory;
    }

    return workflowSetting.IsEditable ? CustomFieldAccessLevel.Editable : CustomFieldAccessLevel.ReadOnly;
};

const getFieldsByIntegrationCode = (
    templates: selectors.types.ExpandedTemplate[],
    field: backend.NetSuiteCompanyCustomField
) => {
    const workflowTemplatesNetSuite = templates.filter((template) => template.integrationCode?.startsWith('NetSuite'));

    const fieldWorkflowsByIntegtrationCode = field.Workflows.reduce<FieldsByIntegrationCode>((result, workflowData) => {
        const { WorkflowId } = workflowData;

        const workflowWithField = workflowTemplatesNetSuite.find((workflowTemplate) =>
            workflowTemplate?.id ? workflowTemplate?.id === WorkflowId : false
        );

        if (workflowWithField) {
            const { integrationCode } = workflowWithField;

            if (integrationCode) {
                result[integrationCode] = workflowData;
            }
        }

        return result;
    }, {});

    return size(fieldWorkflowsByIntegtrationCode) !== 0 ? fieldWorkflowsByIntegtrationCode : null;
};

export const mapSelectNetSuiteFieldAnswerToState = (
    data: backend.SelectNetSuiteCustomCompanyFieldsAnswer,
    netSuiteFieldsReference: NetSuiteCustomFieldReferenceItem[],
    templates: selectors.types.ExpandedTemplate[],
    fieldType: CustomFieldType
): CustomFieldsStateItem[] => {
    return (data?.Fields || [])
        .filter((field) => isCorrectCustomFieldType(fieldType, field.NetSuiteField.Field.ScriptId))
        .sort((field1, field2) => field1.NetSuiteField.Order - field2.NetSuiteField.Order)
        .map((field) => {
            const referenceValue =
                netSuiteFieldsReference.find(
                    (referenceItem) =>
                        referenceItem.id === field.NetSuiteField.Field.Id &&
                        referenceItem.fieldScriptId === field.NetSuiteField.Field.ScriptId
                ) ?? null;

            if (referenceValue) {
                referenceValue.level = field.NetSuiteField.Level;
            }

            const fieldWorkFlowsByIntegrationCode = getFieldsByIntegrationCode(templates, field);

            const usedInWorkflows =
                fieldWorkFlowsByIntegrationCode &&
                (Object.entries(fieldWorkFlowsByIntegrationCode)
                    .filter(([_, { IsUsed }]) => IsUsed)
                    .map(([integrationCode]) => integrationCode) as domain.IntegrationCode[]);

            return {
                id: uniqueId('netSuiteCustomField-'),
                originalId: field.FieldId || null,
                referenceValue,
                approvalmaxFieldName: field.Name || '',
                vendorBillWorkflowAccessLevel: mapWorkflowFieldAccessLevel(
                    fieldWorkFlowsByIntegrationCode?.[domain.IntegrationCode.NetSuiteBill]
                ),
                poWorkflowAccessLevel: mapWorkflowFieldAccessLevel(
                    fieldWorkFlowsByIntegrationCode?.[domain.IntegrationCode.NetSuitePO]
                ),
                expenseReportWorkflowAccessLevel: mapWorkflowFieldAccessLevel(
                    fieldWorkFlowsByIntegrationCode?.[domain.IntegrationCode.NetSuiteExpenseReport]
                ),
                salesOrderWorkflowAccessLevel: mapWorkflowFieldAccessLevel(
                    fieldWorkFlowsByIntegrationCode?.[domain.IntegrationCode.NetSuiteSalesOrder]
                ),
                vraWorkflowAccessLevel: mapWorkflowFieldAccessLevel(
                    fieldWorkFlowsByIntegrationCode?.[domain.IntegrationCode.NetSuiteVRA]
                ),
                billPaymentWorkflowAccessLevel: mapWorkflowFieldAccessLevel(
                    fieldWorkFlowsByIntegrationCode?.[domain.IntegrationCode.NetSuiteBillPayment]
                ),
                raWorkflowAccessLevel: mapWorkflowFieldAccessLevel(
                    fieldWorkFlowsByIntegrationCode?.[domain.IntegrationCode.NetSuiteRA]
                ),
                invoiceWorkflowAccessLevel: mapWorkflowFieldAccessLevel(
                    fieldWorkFlowsByIntegrationCode?.[domain.IntegrationCode.NetSuiteInvoice]
                ),
                journalEntryWorkflowAccessLevel: mapWorkflowFieldAccessLevel(
                    fieldWorkFlowsByIntegrationCode?.[domain.IntegrationCode.NetSuiteJournalEntry]
                ),
                vendorWorkflowAccessLevel: mapWorkflowFieldAccessLevel(
                    fieldWorkFlowsByIntegrationCode?.[domain.IntegrationCode.NetSuiteVendor]
                ),
                canClone: false,
                canSplit: false,
                canRemove: true,
                usedInWorkflows,
            };
        });
};

export const validateFields = (fields: CustomFieldsStateItem[]) => {
    return !fields.some((field) => !field.referenceValue);
};

export const getFieldAccessLevelValue = (accessLevel: CustomFieldAccessLevel) => {
    return customFieldAccessLevels.find((item) => item.id === accessLevel) ?? null;
};

const isFieldUsedInCurrentWorkflow = (
    currentField: CustomFieldsStateItem | undefined,
    currentWorkflow: domain.IntegrationCode
) => {
    return currentField?.usedInWorkflows?.includes(currentWorkflow);
};

export const showFieldUsedInNotification = (integrationsList: domain.IntegrationCode[], fieldName?: string) => {
    const workflowList: string[] = [];

    integrationsList.forEach((workflowIntegrationCode) => {
        workflowList.push(selectors.template.getTemplateDisplayNameByCode(workflowIntegrationCode) || '');
    });

    notificationService.showErrorToast(
        messages.usedInMatrixError({
            fieldName,
            workflowList: workflowList.join(', '),
        })
    );
};

export const validateFieldChanges = (params: ValidateFieldChangesArgs) => {
    const { newFieldState, currentFieldsState, integrationCode, li } = params;

    // prevent changes the fied state if it used in workflows:
    if (newFieldState === CustomFieldAccessLevel.Hidden) {
        const currentField = currentFieldsState.find(({ id }) => id === li.id);

        if (isFieldUsedInCurrentWorkflow(currentField, integrationCode)) {
            showFieldUsedInNotification([integrationCode], currentField?.referenceValue?.fieldName);

            return false;
        }
    }

    return true;
};

export const getAccessLevelValue = (
    valueKey: AccessLevelFieldKeys,
    integrationCode: domain.IntegrationCode,
    setCustomFieldsState: Dispatch<SetStateAction<CustomFieldsStateItem[]>>
) => {
    return (li: CustomFieldsStateItem) => (
        <Select
            size='small'
            spacing='12 8'
            bordered={false}
            clearable={false}
            itemNameKey='text'
            items={customFieldAccessLevels}
            value={li[valueKey]}
            onChange={(valueId: CustomFieldAccessLevel | undefined) => {
                setCustomFieldsState((current) => {
                    const isChangeEnable = validateFieldChanges({
                        currentFieldsState: current,
                        integrationCode,
                        li,
                        newFieldState: valueId,
                    });

                    if (!isChangeEnable) return current;

                    return current.map((item) => {
                        if (item.id === li.id) {
                            return {
                                ...item,
                                [valueKey]: valueId ?? CustomFieldAccessLevel.Editable,
                            };
                        }

                        return item;
                    });
                });
            }}
        />
    );
};

export const getAvailableIntegrationCodes = (companyBetaFeatures: domain.CompanyBetaFeature[]) => {
    return netSuiteIntegrationCodes.reduce<domain.NetSuiteIntegrationCodeType[]>(
        (integrationCodes, { integrationCode, betaFeature }) => {
            if (!betaFeature || companyBetaFeatures.includes(betaFeature)) {
                integrationCodes.push(integrationCode);
            }

            return integrationCodes;
        },
        []
    );
};

export const getWorkflowAccessLevelType = (integrationCode: domain.NetSuiteIntegrationCodeType) =>
    netSuiteAccessLevelTypes[integrationCode];

export const mapFieldWorkflow = (
    workflowId: string,
    item: CustomFieldsStateItem,
    workflowAccessLevelType: WorkflowAccessLevelType
): backend.transfers.AddOrUpdateNetSuiteFieldsTransfer['fields'][number]['fieldWorkflows'][number] => {
    return {
        workflowId,
        isActive: item[workflowAccessLevelType] !== CustomFieldAccessLevel.Hidden,
        isEditable: [CustomFieldAccessLevel.Editable, CustomFieldAccessLevel.Mandatory].includes(
            item[workflowAccessLevelType]
        ),
        isMandatory: item[workflowAccessLevelType] === CustomFieldAccessLevel.Mandatory,
    };
};
